import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import '../css/financeiro-parcela-pagar.css';

/**
 * Origem ou destino de uma opção de aéreo na tabela: uma perna por linha, cada uma numa linha só.
 *
 * Desde a importação da malha, o texto gravado na opção traz a cidade e o aeroporto ("Salvador
 * (Deputado Luiz Eduardo Magalhães)"; antes de 28/10/2026, "Salvador - Deputado..."), longo o
 * bastante para quebrar dentro da célula e desalinhar a perna da data, do voo e dos horários ao
 * lado. Aqui a perna nunca quebra e, passando de 280px (`.fin-pp__aereo-local`), corta com
 * reticências. Só quando alguma perna foi cortada a célula vira clicável e abre o texto inteiro num
 * popover, e não no `title`, que o médico não descobre e some ao mexer o mouse.
 *
 * O `OverlayTrigger` fica sempre na árvore, só sem gatilho enquanto nada foi cortado: trocar a
 * árvore ao cortar remontaria o `<span>`, e o `ResizeObserver` ficaria preso no nó que saiu.
 *
 * @param {string} valor as pernas separadas por "|", como a opção grava
 */
export default function LocalAereo({ valor }) {
    const ref = useRef(null);
    const [cortado, setCortado] = useState(false);
    const pernas = String(valor ?? '').split('|').map(p => p.trim());

    const medir = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        setCortado(Array.from(el.children).some(c => c.scrollWidth > c.clientWidth));
    }, []);

    // A largura da coluna depende das outras linhas da tabela e da janela: mede de novo quando ela muda.
    useLayoutEffect(() => {
        medir();
        if (typeof ResizeObserver === 'undefined' || !ref.current) return undefined;
        const obs = new ResizeObserver(medir);
        obs.observe(ref.current);
        return () => obs.disconnect();
    }, [medir, valor]);

    // `fin-pp` no popover: ele vai para o `body`, fora do escopo dos tokens.
    const popover = (
        <Popover id="local-aereo" className="fin-pp" style={{ maxWidth: 'calc(100vw - 32px)' }}>
            <Popover.Content style={{ fontSize: 13, color: 'var(--fin-tx)' }}>
                {pernas.map((p, i) => <div key={i}>{p}</div>)}
            </Popover.Content>
        </Popover>
    );
    // Cortado, o clique (ou o toque) é do popover e não marca a opção: o card do celular é o `label` do
    // rádio, e o toque no texto o marcaria.
    return (
        <OverlayTrigger trigger={cortado ? 'click' : []} placement="bottom-start" rootClose container={document.body} overlay={popover}>
            <span ref={ref} className={'fin-pp__aereo-local' + (cortado ? ' is-cortado' : '')}
                onClick={cortado ? (e) => { e.preventDefault(); e.stopPropagation(); } : undefined}>
                {pernas.map((p, i) => <span key={i}>{p || ' '}</span>)}
            </span>
        </OverlayTrigger>
    );
}
